import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type {
  BaseProductDto,
  GroupedProduct,
} from "@/types/product/productTypes";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import Product from "@/api/classes/product/product";
import { useProductsTableStore } from "@/stores/product/products/productsTable";
import { getLinks } from "@/tools/createLinkMarketPlace/createLinkToMarketPlace";

export interface SyncResiduesResponse {
  Wildberries: string | "Ok";
  Ozon: string | "Ok";
  [key: string]: string | "Ok";
}

export const useProductsStore = defineStore("products", () => {
  const products = ref<BaseProductDto[][]>([]);
  const getProductsStatusLoad = ref(enumStatusLoad.NOT_STARTED);

  // Пагинация
  const pagination = ref({
    current: 1,
    total: 500,
    pageSize: 10,
    defaultPageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} из ${total} товаров`,
    onChangePagination: async () => {
      await getProducts();
    },
  });

  const productsUnitedByWBNomenclatureId = computed<GroupedProduct[]>(() => {
    return products.value.map((group: BaseProductDto[]) => {
      const combinedProduct: GroupedProduct = {
        ...group[0],
        size: group.map((product) => product.size),
        barcode: group.map((product) => product.barcode),
        ids: group.map((product) => product.id),
        linksToMarkets: group.map((g) =>
          getLinks({
            ozonArticle: g.ozonArticle || "",
            wbArticle: g.wbArticle || "",
            wbSiseId: g.chrtId || null,
            chZnLink: g.chestniyZnakProductLink || "",
          }),
        ),
        residueAllFBO: group.map(
          (cur) => cur.ozonFBO + cur.wildberriesFBO + cur.yandexFBO,
        ),
        ozonFBO: group.map((cur) => cur.ozonFBO),
        wildberriesFBO: group.map((cur) => cur.wildberriesFBO),
        yandexFBO: group.map((cur) => cur.yandexFBO),
        fbsResidues: group.map((cur) => cur.fbsResidues),
        wbBarcode: group.map((product) => product.wbBarcode),
        ozonBarcode: group.map((product) => product.ozonBarcode),
      };

      return combinedProduct;
    });
  });

  const getProducts = async () => {
    const productsTable = useProductsTableStore();

    try {
      getProductsStatusLoad.value = enumStatusLoad.LOADING;

      const take = pagination.value.pageSize;
      const skip = (pagination.value.current - 1) * pagination.value.pageSize;

      const response = await Product.getProductsForTable(take, skip, {
        searchPhrase: productsTable.filter.searchPhrase,
        createdInOzon: productsTable.filter.createdInOzon
          ? true
          : productsTable.filter.notCreatedInOzon
            ? false
            : null,
        createdInWb: productsTable.filter.createdInWb
          ? true
          : productsTable.filter.notCreatedInWb
            ? false
            : null,
        outOfStockFBS:
          productsTable.filter.outOfStockFBS === true ? true : null,
        outOfStockFBO:
          productsTable.filter.outOfStockFBO === true ? true : null,
        readyToExport: productsTable.filter.readyToExport,
        duplicate: null,
        sortedField: productsTable.filter.sortedField,
        isOrderByDesc: productsTable.filter.isOrderByDesc,
        brands: productsTable.filter.brands,
        categories: productsTable.filter.categories,
      });

      if (response.status === 200) {
        productsTable.selectedRows = [];
        products.value = Object.values(response.data.item1);
        pagination.value.total = response.data.item2;
      }
      getProductsStatusLoad.value = enumStatusLoad.LOADED;
    } catch (error: any) {
      getProductsStatusLoad.value = enumStatusLoad.ERROR;
      console.error(error);
    }
  };

  const syncResidues = (baseProductIdToSync: string) =>
    Product.syncResidues(baseProductIdToSync);

  const $reset = () => {
    products.value.length = 0;
  };

  return {
    products,
    productsUnitedByWBNomenclatureId,
    getProductsStatusLoad,
    getProducts,
    pagination,
    syncResidues,
    $reset,
  };
});
